import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", address: "", gstin: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/suppliers", { params: q ? { q } : {} });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, phone: s.phone, email: s.email, address: s.address, gstin: s.gstin }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Supplier updated" : "Supplier added");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    await api.delete(`/suppliers/${s.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Suppliers</h1>
          <p className="text-sm text-slate-500">{items.length} distributors &amp; wholesalers on record.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-supplier-btn" onClick={openNew} className="bg-emerald-900 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-2" /> Add Supplier</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Truck className="w-4 h-4 text-emerald-700" /> {editing ? "Edit" : "New"} Supplier</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Supplier Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="sup-name" />
              <Field label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} />
              <Field label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} />
              <Field label="Address" v={form.address} on={(v) => setForm({ ...form, address: v })} />
              <Field label="GSTIN" v={form.gstin} on={(v) => setForm({ ...form, gstin: v })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.name} data-testid="save-supplier-btn" className="bg-emerald-900 hover:bg-emerald-800">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier…" className="pl-9" />
        </div>
      </Card>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table data-testid="suppliers-table">
            <TableHeader><TableRow>
              <TableHead>Supplier</TableHead><TableHead>Phone</TableHead><TableHead>GSTIN</TableHead><TableHead>Address</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell><div className="font-semibold">{s.name}</div><div className="text-xs text-slate-500">{s.email}</div></TableCell>
                  <TableCell className="font-mono-num">{s.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{s.gstin || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{s.address || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`delete-supplier-${s.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No suppliers yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, v, on, testid }) {
  return (<div><Label className="text-xs">{label}</Label><Input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="mt-1 h-10" /></div>);
}
